import { css } from '@emotion/react';

import { colors } from '../theme/colors';
import { media } from '../theme/media';
import { fontWeights } from '../theme/type';
import { rem } from '../utilities/style';

import Button from '../components/buttons/Button';
import ButtonBlock from '../components/ButtonBlock';

// Necessary to allow selecting first rich text element to remove spacing. This
// warning is because emotion adds style tag as first element in SSR making the
// selector invalid. We ignore style tags in the selector to avoid this issue.
// https://github.com/emotion-js/emotion/pull/1209
const ignoreSsrFlag = '/* emotion-disable-server-rendering-unsafe-selector-warning-please-do-not-use-this-the-warning-exists-for-a-reason */';

export const headers = {
  h1: css`
    font-size: ${rem(40)};
    font-weight: ${fontWeights.bold};
    line-height: 1.15;
    letter-spacing: -0.02em;

    ${media('>tiny')} {
      font-size: ${rem(48)};
    }

    ${media('>medium')} {
      font-size: ${rem(62)};
    }
  `,
  h2: css`
    font-size: ${rem(36)};
    font-weight: ${fontWeights.bold};
    line-height: 1.15;
    letter-spacing: -0.02em;

    ${media('>tiny')} {
      font-size: ${rem(44)};
    }

    ${media('>medium')} {
      font-size: ${rem(48)};
    }
  `,
  h3: css`
    font-size: ${rem(28)};
    font-weight: ${fontWeights.bold};
    line-height: 1.33;

    ${media('>tiny')} {
      font-size: ${rem(32)};
    }

    ${media('>medium')} {
      font-size: ${rem(40)};
    }
  `,
  h4: css`
    font-weight: ${fontWeights.bold};
    font-size: ${rem(24)};
    line-height: 1.33;
  `,
  h5: css`
    font-weight: ${fontWeights.semiBold};
    font-size: ${rem(22)};
    line-height: 1.33;
  `,
  h6: css`
    font-weight: ${fontWeights.semiBold};
    font-size: ${rem(18)};
    line-height: 1.33;
  `,
  h7: css`
    font-weight: normal;
    font-size: ${rem(14)};
    line-height: 1.33;
  `,
  eyebrow: css`
    font-weight: ${fontWeights.medium};
    font-size: ${rem(16)};
    line-height: 1.2;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  `
};

export const body = {
  regular: css`
    font-size: ${rem(16)};
    line-height: 1.55;
  `,
  small: css`
    font-size: ${rem(14)};
    line-height: 1.4;
  `,
  large: css`
    font-size: ${rem(20)};
    font-weight: ${fontWeights.medium};
    line-height: 1.46;
  `,
  ul: css`
    list-style: outside;
    margin-left: ${rem(20)};

    li::marker {
      color: ${colors.blue['500']};
    }

    li + li {
      margin-top: ${rem(12)};
    }
  `,
  ol: css`
    list-style: decimal outside;
    margin-left: ${rem(20)};

    ol {
      list-style: lower-alpha outside;

      ol {
        list-style: lower-roman outside;
      }
    }

    li::marker {
      color: ${colors.blue['500']};
      font-weight: 600;
    }

    li + li {
      margin-top: ${rem(12)};
    }
  `
};

export const linkStyles = ({ light, lighter, theme }) => css`
  a:not(${Button}) {
    font-weight: ${fontWeights.medium};
    ${!light && !lighter && `
      color: ${colors.blue['800']};
    `}
    ${light && `
      color: ${colors.gray['200']};
    `}
    ${lighter && `
      color: ${colors.gray['50']};
    `}
    ${theme.pageOverrides?.includes('Dark Mode') && `
      color: ${colors.blue['100']};
    `}
    text-decoration: underline;
    overflow-wrap: break-word;

    &:hover {
      text-decoration: none;
    }
  }
`;

export const richTextStyles = ({ light, lighter, theme }) => css`
  &::after {
    content: '';
    clear: both;
    display: table;
  }

  p:empty {
    display: none;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  ul,
  ol,
  blockquote,
  pre {
    margin: ${rem(24)} 0;
  }

  p + p {
    margin-top: 1em;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    ${(!light && !lighter) && `
      color: ${colors.blue['800']};
    `}
    ${(light || lighter) && `
      color: ${colors.white};
    `}
    ${theme.pageOverrides?.includes('Dark Mode') && `
      color: ${colors.blue['100']};
    `}
  }

  h1 {
    ${headers.h2}
  }

  h2 {
    ${headers.h3}
  }

  h3 {
    ${headers.h4}
  }

  h4 {
    ${headers.h5}
  }

  h5 {
    ${headers.h6}
  }

  h6 {
    ${headers.h7}
    font-weight: ${fontWeights.semiBold};
  }

  ${linkStyles({ light, lighter, theme })}

  p,
  ul,
  ol,
  blockquote,
  pre {
    ${!light && !lighter && `
      color: ${colors.gray['600']};
    `}
    ${light && `
      color: ${colors.gray['200']};
    `}
    ${lighter && `
      color: ${colors.gray['50']};
    `}
    ${theme.pageOverrides?.includes('Dark Mode') && `
      color: ${colors.gray['400']};
    `}
  }

  ul {
    ${body.ul}

    ul,
    ol {
      margin: ${rem(8)} 0 ${rem(8)} ${rem(20)};
    }
  }

  ol {
    ${body.ol}

    ul,
    ol {
      margin: ${rem(8)} 0 ${rem(8)} ${rem(20)};
    }
  }

  hr {
    height: 1px;
    border: none;
    background: ${colors.gray['200']};
    margin: ${rem(40)} 0;
  }

  > * {
    &:first-child:not(style) ${ignoreSsrFlag} {
      margin-top: 0;
    }
  }

  > *:last-child {
    margin-bottom: 0;
  }

  ${Button} {
    text-decoration: none;
  }

  ${ButtonBlock} + ${ButtonBlock} {
    padding-top: 0;
  }

  div[class*="Content-shadow-regular"] .hero-wrapper {
    margin-left: 0;
    transform: translateX(0%);
  }
`;

export const shortRichTextStyles = ({ light, lighter, theme }) => css`
  &::after {
    content: '';
    clear: both;
    display: table;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  ul,
  ol {
    margin-bottom: ${rem(8)};
  }

  p + p {
    margin-top: 0.5em;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    ${(!light && !lighter) && `
      color: ${colors.blue['800']};
    `}
    ${(light || lighter) && `
      color: ${colors.white};
    `}
    ${theme.pageOverrides?.includes('Dark Mode') && `
      color: ${colors.blue['100']};
    `}
  }

  h2 {
    ${headers.h4}
  }

  h3 {
    ${headers.h5}
  }

  h4 {
    ${headers.h6}
  }

  h5 {
    ${headers.h7}
    font-weight: ${fontWeights.semiBold};
  }

  h6 {
    ${headers.h7}
  }

  ${linkStyles({ light, lighter, theme })}

  p,
  ul,
  ol {
    ${!light && !lighter && `
      color: ${colors.gray['600']};
    `}
    ${light && `
      color: ${colors.gray['200']};
    `}
    ${lighter && `
      color: ${colors.gray['50']};
    `}
    ${theme.pageOverrides?.includes('Dark Mode') && `
      color: ${colors.gray['400']};
    `}
  }

  ul,
  ol {
    margin-left: ${rem(20)};

    li::marker {
      color: ${colors.blue['500']};
    }

    li + li {
      margin-top: ${rem(4)};
    }
  }

  ul {
    list-style: outside;
  }

  ol {
    list-style: decimal outside;

    li::marker {
      font-weight: 600;
    }
  }

  > *:last-child {
    margin-bottom: 0;
  }
`;
